/* eslint-disable  @exodus/export-default/last */

import { calculateBumpedGasPrice, isEthereumLikeToken, normalizeTxId } from '@exodus/ethereum-lib'
import assert from 'minimalistic-assert'

import * as ErrorWrapper from '../error-wrapper.js'
import { transactionExists } from '../eth-like-util.js'
import { getNftArguments } from '../nft-utils.js'
import getFeeInfo from './get-fee-info.js'
import { resolveNonce } from './nonce-utils.js'

const txSendFactory = ({ assetClientInterface, createUnsignedTx }) => {
  assert(assetClientInterface, 'assetClientInterface is required')
  assert(createUnsignedTx, 'createUnsignedTx is required')
  return async ({ asset, walletAccount, address, amount, options = {} }) => {
    const {
      nft,
      bumpTxId,
      nonce: providedNonce,
      customFee,
      keepTxInput,
      isSendAll,
      isHardware,
    } = options
    let { txInput } = options // avoid let!

    const feeOpts = {
      gasPrice: options.gasPrice,
      tipGasPrice: options.tipGasPrice,
      gasLimit: options.gasLimit,
    }

    const assetName = asset.name
    const baseAsset = asset.baseAsset

    const assets = await assetClientInterface.getAssetsForNetwork({ baseAssetName: baseAsset.name })

    const fromAddress = await assetClientInterface.getReceiveAddress({
      assetName: baseAsset.name,
      walletAccount,
    })
    const feeData = await assetClientInterface.getFeeData({
      assetName: baseAsset.name,
    })

    let contractAddress
    if (nft) {
      const nftArguments = await getNftArguments({ asset, nft, fromAddress, toAddress: address })
      contractAddress = nftArguments.contractAddress
      feeOpts.gasLimit = nftArguments.gasLimit
      txInput = nftArguments.txInput
      amount = asset.baseAsset.currency.ZERO
    }

    let bumpNonce

    let eip1559Enabled = feeData.eip1559Enabled

    const baseAssetTxLog = await assetClientInterface.getTxLog({
      assetName: baseAsset.name,
      walletAccount,
    })

    // `replacedTx` is always an ETH/ETC transaction (not a token)
    let replacedTx, replacedTokenTx
    if (bumpTxId) {
      replacedTx = baseAssetTxLog.get(bumpTxId)
      if (!replacedTx || !replacedTx.pending) {
        throw new Error(`Cannot bump transaction ${bumpTxId}: not found or confirmed`)
      }

      if (replacedTx.tokens.length > 0) {
        const tokenTxSet = await assetClientInterface.getTxLog({
          assetName: replacedTx.tokens[0],
          walletAccount,
        })
        replacedTokenTx = tokenTxSet.get(bumpTxId)

        if (replacedTokenTx) {
          asset = assets[replacedTx.tokens[0]]
        }
      }

      address = (replacedTokenTx || replacedTx).to
      amount = (replacedTokenTx || replacedTx).coinAmount.negate()
      feeOpts.gasLimit = replacedTx.data.gasLimit

      const { gasPrice: currentGasPrice } = feeData
      const { bumpedGasPrice, bumpedTipGasPrice } = calculateBumpedGasPrice({
        baseAsset,
        tx: replacedTx,
        currentGasPrice,
        eip1559Enabled,
      })
      feeOpts.gasPrice = bumpedGasPrice
      feeOpts.tipGasPrice = bumpedTipGasPrice
      eip1559Enabled = feeData.eip1559Enabled && feeOpts.tipGasPrice
      bumpNonce = replacedTx.data.nonce
      txInput = replacedTokenTx ? null : replacedTx.data.data || '0x'
      if (bumpNonce === undefined) {
        throw new Error(`Cannot bump transaction ${bumpTxId}: data object seems to be corrupted`)
      }
    }

    const resolvedNonce =
      bumpNonce ??
      (await resolveNonce({ asset, fromAddress, providedNonce, txLog: baseAssetTxLog }))

    const createTxParams = {
      assetClientInterface,
      asset,
      walletAccount,
      toAddress: contractAddress || address,
      amount,
      nonce: resolvedNonce,
      fromAddress,
      eip1559Enabled,
      customFee,
      feeOpts,
      txInput,
      keepTxInput,
      isSendAll,
      createUnsignedTx,
    }
    let { txId, rawTx, nonce, gasLimit, tipGasPrice, gasPrice } = await createTx(createTxParams)

    const feeAmount = gasPrice.mul(gasLimit)

    try {
      await baseAsset.api.broadcastTx(rawTx.toString('hex'))
    } catch (err) {
      const nonceTooLowErr = err.message.match(/nonce (is |)too low/i)
      const insufficientFundsErr = err.message.match(/insufficient funds/i)
      const txAlreadyExists = nonceTooLowErr
        ? await transactionExists({ asset, txId })
        : err.message.match(/already known/i)

      if (txAlreadyExists) {
        console.info('tx already broadcast') // inject logger factory from platform
      } else if (insufficientFundsErr) {
        throw new ErrorWrapper.EthLikeError({
          message: err.message,
          reason: ErrorWrapper.reasons.insufficientFunds,
          hint: 'broadcastTx',
        })
      } else if (bumpTxId) {
        throw new ErrorWrapper.EthLikeError({
          message: err.message,
          reason: ErrorWrapper.reasons.bumpTxFailed,
          hint: 'broadcastTx',
        })
      } else if (!nonceTooLowErr) {
        throw new ErrorWrapper.EthLikeError({
          message: err.message,
          reason: ErrorWrapper.reasons.broadcastTxFailed,
          hint: 'otherErr:broadcastTx',
        })
      } else if (nonceTooLowErr && !isHardware) {
        console.info('trying to send again...') // inject logger factory from platform
        // let's try to fix the nonce issue
        nonce = await resolveNonce({
          asset,
          fromAddress,
          providedNonce,
          txLog: baseAssetTxLog,
          triedNonce: nonce,
          forceFromNode: true,
        })
        ;({ txId, rawTx } = await createTx({ ...createTxParams, nonce }))

        try {
          await baseAsset.api.broadcastTx(rawTx.toString('hex'))
        } catch (err) {
          const insufficientFundsErr = err.message.match(/insufficient funds/i)
          if (insufficientFundsErr) {
            throw new ErrorWrapper.EthLikeError({
              message: err.message,
              reason: ErrorWrapper.reasons.insufficientFunds,
              hint: 'retry:broadcastTx',
            })
          }

          throw new ErrorWrapper.EthLikeError({
            message: err.message,
            reason: ErrorWrapper.reasons.broadcastTxFailed,
            hint: 'retry:broadcastTx',
          })
        }
      }
    }

    const selfSend = fromAddress === address

    await assetClientInterface.updateTxLogAndNotify({
      assetName: asset.name,
      walletAccount,
      txs: [
        {
          txId,
          confirmations: 0,
          coinAmount: selfSend ? asset.currency.ZERO : amount.abs().negate(),
          coinName: asset.name,
          feeAmount,
          feeCoinName: asset.feeAsset.name,
          selfSend,
          to: address,
          currencies: {
            [assetName]: asset.currency,
            [asset.feeAsset.name]: asset.feeAsset.currency,
          },
          data: eip1559Enabled
            ? {
                gasLimit,
                replacedTxId: bumpTxId,
                nonce,
                ...(tipGasPrice
                  ? { tipGasPrice: tipGasPrice.toBaseString() }
                  : Object.create(null)),
              }
            : { gasLimit, replacedTxId: bumpTxId, nonce },
        },
      ],
    })

    const isToken = isEthereumLikeToken(asset)
    if (isToken) {
      await assetClientInterface.updateTxLogAndNotify({
        assetName: baseAsset.name,
        walletAccount,
        txs: [
          {
            txId,
            coinAmount: baseAsset.currency.ZERO,
            coinName: baseAsset.name,
            feeAmount,
            feeCoinName: baseAsset.name,
            selfSend,
            to: address,
            token: asset.name,
            currencies: {
              [baseAsset.name]: baseAsset.currency,
              [asset.feeAsset.name]: asset.feeAsset.currency,
            },
            data: eip1559Enabled
              ? {
                  gasLimit,
                  replacedTxId: bumpTxId,
                  nonce,
                  ...(tipGasPrice ? { tipGasPrice: tipGasPrice.toBaseString() } : {}),
                }
              : { gasLimit, replacedTxId: bumpTxId, nonce },
          },
        ],
      })
    }

    return { txId, nonce }
  }
}

const createTx = async ({
  assetClientInterface,
  asset,
  walletAccount,
  toAddress,
  amount,
  nonce,
  txInput,
  eip1559Enabled = true,
  keepTxInput = false,
  customFee: customGasPrice,
  isSendAll,
  fromAddress,
  feeOpts,
  createUnsignedTx,
}) => {
  assert(
    nonce !== undefined && typeof nonce === 'number',
    'Nonce must be provided when creating a tx'
  )
  const isToken = isEthereumLikeToken(asset)

  if (txInput && isToken && !keepTxInput)
    throw new Error(`Additional data for Ethereum Token (${asset.name}) is not allowed`)

  txInput =
    isToken && !keepTxInput
      ? asset.contract.transfer.build(toAddress.toLowerCase(), amount.toBaseString())
      : txInput

  let { gasLimit, gasPrice, tipGasPrice } = await getFeeInfo({
    assetClientInterface,
    asset,
    fromAddress,
    toAddress,
    amount,
    txInput,
    feeOpts,
  })

  if (eip1559Enabled) {
    if (customGasPrice) {
      gasPrice = customGasPrice // aka maxFeePerGas

      // We must ensure maxPriorityFeePerGas <= maxFeePerGas or our transaction library throws an error
      // It's a bit counterintuitive  since maxPriorityFeePerGas should only contain the tip,
      // so we should be subtracting the base gas price from the custom gas price to keep just the tip
      // but the fee is also limited by our maxFeePerGas above, so that implicitly captures the max tip.
      // Setting this tipGasPrice to undefined will result in a legacy transaction (not an EIP1559 anymore)
      tipGasPrice = customGasPrice // aka maxPriorityFeePerGas
    }

    if (isSendAll && !isToken) {
      // force consuming all gas
      tipGasPrice = gasPrice
    }

    // gasLimit = customGasLimit
  }

  const unsignedTx = await createUnsignedTx({
    asset,
    walletAccount,
    address: toAddress,
    amount,
    nonce,
    txInput,
    gasLimit,
    gasPrice,
    tipGasPrice,
    fromAddress,
    eip1559Enabled,
  })

  // TODO: move into createUnsignedTx()
  if (keepTxInput && !isToken) {
    unsignedTx.txData.to = toAddress
  }

  unsignedTx.txMeta.eip1559Enabled = eip1559Enabled

  const { txId, rawTx } = await assetClientInterface.signTransaction({
    assetName: asset.baseAsset.name,
    unsignedTx,
    walletAccount,
  })

  return {
    txId: normalizeTxId(txId),
    rawTx,
    nonce,
    gasLimit,
    gasPrice,
    tipGasPrice,
  }
}

export default txSendFactory
